import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { LIST_PRICING_PLAN_CAPS_RESOURCE } from 'src/features/pricingPlan/api/caps/useListPricingPlanCaps';
import { useUpdatePricingPlanCap } from 'src/features/pricingPlan/api/caps/useUpdatePricingPlanCap';
import CapFormModal from 'src/features/pricingPlan/components/form/caps/CapFormModal';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PricingPlanCap } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface UpdatePricingPlanCapFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedCap: PricingPlanCap;
    children?: React.ReactNode;
}

function UpdatePricingPlanCapFormModal<TData>({
    className,
    semantics,
    children,
    selectedCap,
}: UpdatePricingPlanCapFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdatePricingPlanCap({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PRICING_PLAN_CAPS_RESOURCE] });
            toast({
                title: formatMessage(pricingPlanMessages.updateCapSuccess),
                description: formatMessage(pricingPlanMessages.updateCapSuccessDescription),
            });
        },
    });

    return (
        <CapFormModal
            selectedCap={selectedCap}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ planId, capId: selectedCap.id, parkingId, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.editCapSubtitle)}
        >
            {children}
        </CapFormModal>
    );
}

export default UpdatePricingPlanCapFormModal;
